import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { InputTextarea } from 'primereact/inputtextarea';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import supportTicketService from '../../services/supportTicketService';

/**
 * MyIssues — reporter's view of their own tickets + thread.
 *
 * Two states in one page (toggled by selectedRef):
 *   1. List view  — paginated tickets the user raised, sortable by status
 *   2. Thread view — full conversation + reply box (reporter can reply
 *                     until the ticket is closed)
 *
 * Server enforces visibility: reporters only get their own tickets.
 * Admins get the full inbox via this same endpoint, but admins also have
 * the dedicated AdminInbox page (PR 8). This page is reporter-first.
 *
 * Internal admin notes never appear here — server filters them out for
 * non-admin viewers (see SupportTicketController::show).
 */
const MyIssues = () => {
  const toastRef = useRef(null);

  // ── List view state ─────────────────────────────────────────────────
  const [tickets, setTickets] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // all|open|closed

  // ── Thread view state ───────────────────────────────────────────────
  const [selectedRef, setSelectedRef] = useState(null);
  const [thread, setThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);

  // ── Reply composer ──────────────────────────────────────────────────
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  // ── Load list on mount + when status filter changes ─────────────────
  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await supportTicketService.listTickets(params);
      setTickets(res.data);
    } catch (e) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Could not load your tickets',
        detail: e?.response?.data?.message || 'Try refreshing the page.',
        life: 4000,
      });
    } finally {
      setLoadingList(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Open thread ─────────────────────────────────────────────────────
  const openThread = async (ref) => {
    setSelectedRef(ref);
    setThread(null);
    setLoadingThread(true);
    try {
      const data = await supportTicketService.getTicket(ref);
      setThread(data);
    } catch (e) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Could not load the ticket',
        detail: e?.response?.data?.message || 'Try again.',
        life: 4000,
      });
      setSelectedRef(null);
    } finally {
      setLoadingThread(false);
    }
  };

  const backToList = () => {
    setSelectedRef(null);
    setThread(null);
    setReplyText('');
    loadList(); // refresh in case status changed
  };

  // ── Send reply ──────────────────────────────────────────────────────
  const sendReply = async () => {
    const message = replyText.trim();
    if (!message || sendingReply) return;
    setSendingReply(true);
    try {
      await supportTicketService.addReply(selectedRef, { message });
      setReplyText('');
      // Reload the thread to show the new message + any auto-status change.
      const data = await supportTicketService.getTicket(selectedRef);
      setThread(data);
      toastRef.current?.show({
        severity: 'success',
        summary: 'Sent',
        life: 2000,
      });
    } catch (e) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Could not send reply',
        detail: e?.response?.data?.message || 'Try again.',
        life: 4000,
      });
    } finally {
      setSendingReply(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="p-3 md:p-4">
      <Toast ref={toastRef} position="top-right" />

      {/* Page header */}
      <div className="flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h2 className="m-0 text-xl font-bold text-color">My Issues</h2>
          <div className="text-sm text-color-secondary mt-1">
            Tickets you&apos;ve raised through the support chat.
          </div>
        </div>
        {selectedRef && (
          <Button
            label="Back to list"
            icon="pi pi-arrow-left"
            onClick={backToList}
            className="p-button-outlined"
          />
        )}
      </div>

      {!selectedRef && (
        <>
          {/* Status filter */}
          <div className="flex gap-2 mb-3">
            {['all', 'open', 'closed'].map((s) => (
              <Button
                key={s}
                label={s === 'all' ? 'All' : s === 'open' ? 'Active' : 'Closed'}
                onClick={() => setStatusFilter(s)}
                className={statusFilter === s ? '' : 'p-button-outlined'}
                style={{ borderRadius: 999, fontWeight: 600, fontSize: '.84rem' }}
              />
            ))}
          </div>

          {/* List */}
          {loadingList ? (
            <ListSkeleton />
          ) : tickets.length === 0 ? (
            <EmptyState statusFilter={statusFilter} />
          ) : (
            <div className="flex flex-column gap-2">
              {tickets.map((t) => (
                <TicketRow key={t.ticket_ref} ticket={t} onClick={() => openThread(t.ticket_ref)} />
              ))}
            </div>
          )}
        </>
      )}

      {selectedRef && (
        <ThreadView
          thread={thread}
          loading={loadingThread}
          replyText={replyText}
          onReplyTextChange={setReplyText}
          onSendReply={sendReply}
          sending={sendingReply}
        />
      )}
    </div>
  );
};

// ── Subcomponents ────────────────────────────────────────────────────

const TicketRow = ({ ticket, onClick }) => (
  <Card
    onClick={onClick}
    className="cursor-pointer hover:surface-100 transition-colors"
    style={{ borderLeft: `4px solid ${statusColor(ticket.status)}` }}
  >
    <div className="flex align-items-flex-start justify-content-between gap-3 flex-wrap">
      <div className="flex-1 min-w-0">
        <div className="flex align-items-center gap-2 flex-wrap mb-2">
          <span className="font-mono text-xs font-bold text-primary-700">{ticket.ticket_ref}</span>
          <Tag value={humanizeCategory(ticket.category)} severity="info" rounded />
          <StatusTag status={ticket.status} />
        </div>
        <div className="text-sm text-color line-height-3">
          {ticket.note_preview || '(no note)'}
        </div>
      </div>
      <div className="text-right" style={{ minWidth: '8rem' }}>
        <div className="text-xs text-color-secondary">
          {formatRelative(ticket.last_reply_at || ticket.created_at)}
        </div>
        <i className="pi pi-chevron-right text-color-secondary mt-2" aria-hidden="true"></i>
      </div>
    </div>
  </Card>
);

const ThreadView = ({ thread, loading, replyText, onReplyTextChange, onSendReply, sending }) => {
  if (loading) return <ThreadSkeleton />;
  if (!thread) return null;

  const replies = Array.isArray(thread.replies) ? thread.replies : [];
  const isTerminal = thread.status === 'closed';

  return (
    <Card>
      {/* Thread header */}
      <div className="flex align-items-center gap-2 flex-wrap mb-2">
        <span className="font-mono text-sm font-bold text-primary-700">{thread.ticket_ref}</span>
        <Tag value={humanizeCategory(thread.category)} severity="info" rounded />
        <StatusTag status={thread.status} />
      </div>

      {/* Original note */}
      <div
        className="p-3 border-round mb-3"
        style={{ background: 'var(--surface-50)', border: '1px solid var(--surface-border)' }}
      >
        <div className="text-xs text-color-secondary mb-1">You wrote</div>
        <div className="text-sm text-color line-height-3">{thread.note || '(no note added)'}</div>
        <div className="text-xs text-color-secondary mt-2">{formatRelative(thread.created_at)}</div>
      </div>

      {/* Replies */}
      <div className="flex flex-column gap-3 mb-3">
        {replies.length === 0 ? (
          <div className="text-center text-sm text-color-secondary py-3">
            <i className="pi pi-clock mb-2 block" aria-hidden="true"></i>
            <div>Waiting for a reply. We&apos;ll email you when there&apos;s an update.</div>
          </div>
        ) : (
          replies.map((r) => <ReplyBubble key={r.id} reply={r} />)
        )}
      </div>

      {/* Reply composer (hidden when ticket is closed) */}
      {isTerminal ? (
        <div
          className="p-3 border-round text-center text-sm text-color-secondary"
          style={{ background: 'var(--surface-100)' }}
        >
          This ticket is closed. To raise the same issue again, use the
          chat button at the bottom-right of any page.
        </div>
      ) : (
        <div>
          <div className="text-xs text-color-secondary mb-2 font-semibold uppercase" style={{ letterSpacing: '.06em' }}>
            Reply
          </div>
          <div className="flex align-items-flex-end gap-2">
            <InputTextarea
              value={replyText}
              onChange={(e) => onReplyTextChange(e.target.value)}
              placeholder="Type your reply…"
              rows={2}
              autoResize
              disabled={sending}
              className="flex-1"
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                  e.preventDefault();
                  onSendReply();
                }
              }}
            />
            <Button
              label={sending ? 'Sending…' : 'Send'}
              icon={sending ? 'pi pi-spinner pi-spin' : 'pi pi-send'}
              onClick={onSendReply}
              disabled={sending || !replyText.trim()}
            />
          </div>
          <div className="text-xs text-color-secondary mt-2">
            Press <kbd>Ctrl + Enter</kbd> to send.
          </div>
        </div>
      )}
    </Card>
  );
};

const ReplyBubble = ({ reply }) => {
  const fromReporter = reply.author_role === 'reporter';
  return (
    <div className={`flex ${fromReporter ? 'justify-content-end' : 'justify-content-start'}`}>
      <div
        className="p-3 border-round shadow-1"
        style={{
          maxWidth: '78%',
          background: fromReporter ? 'var(--primary-color)' : '#fff',
          color: fromReporter ? '#fff' : 'var(--text-color)',
          borderRadius: fromReporter ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
        }}
      >
        <div className="flex align-items-center gap-2 mb-1 text-xs" style={{ opacity: 0.8 }}>
          <span className="font-bold">{reply.author_name || 'Support'}</span>
          <span>·</span>
          <span>{humanizeRole(reply.author_role)}</span>
        </div>
        <div className="text-sm line-height-3" style={{ whiteSpace: 'pre-wrap' }}>{reply.message}</div>
        <div className="text-xs mt-2" style={{ opacity: 0.7 }}>{formatRelative(reply.created_at)}</div>
      </div>
    </div>
  );
};

const StatusTag = ({ status }) => {
  const map = {
    open:        { value: 'Open',        severity: 'warning' },
    in_progress: { value: 'In Progress', severity: 'info' },
    resolved:    { value: 'Resolved',    severity: 'success' },
    closed:      { value: 'Closed',      severity: 'secondary' },
    reopened:    { value: 'Reopened',    severity: 'danger' },
  };
  const m = map[status] || { value: status, severity: 'info' };
  return <Tag value={m.value} severity={m.severity} rounded />;
};

const ListSkeleton = () => (
  <div className="flex flex-column gap-2">
    {[1, 2, 3].map((i) => (
      <Card key={i}><Skeleton height="3rem" /></Card>
    ))}
  </div>
);

const ThreadSkeleton = () => (
  <Card>
    <Skeleton height="1.5rem" className="mb-3" />
    <Skeleton height="6rem" className="mb-3" />
    <Skeleton height="4rem" />
  </Card>
);

const EmptyState = ({ statusFilter }) => (
  <Card>
    <div className="text-center py-4">
      <i className="pi pi-inbox text-color-secondary mb-3" style={{ fontSize: '2.5rem' }} aria-hidden="true"></i>
      <h3 className="text-color mb-1 text-lg">No tickets here</h3>
      <p className="text-sm text-color-secondary m-0">
        {statusFilter === 'all'
          ? "You haven't raised any tickets yet. Click the blue \"Raise an Issue\" button at the bottom-right to get help."
          : `No ${statusFilter === 'open' ? 'active' : 'closed'} tickets.`}
      </p>
    </div>
  </Card>
);

// ── Helpers ──────────────────────────────────────────────────────────

const humanizeCategory = (key) => ({
  borrower_reports: 'Borrower Reports',
  offer_generation: 'Offer Generation',
  credits_issue:    'Credits Issue',
  others:           'Others',
}[key] || key);

const humanizeRole = (role) => ({
  reporter:     'You',
  tenant_admin: 'Admin',
  super_admin:  'Support team',
}[role] || 'Support');

const statusColor = (status) => ({
  open:        '#f59e0b',
  in_progress: '#3b82f6',
  resolved:    '#10b981',
  closed:      '#94a3b8',
  reopened:    '#ef4444',
}[status] || '#94a3b8');

const formatRelative = (iso) => {
  if (!iso) return '';
  const then = new Date(iso);
  const now = new Date();
  const ms = now - then;
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr${hr > 1 ? 's' : ''} ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day > 1 ? 's' : ''} ago`;
  return then.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default MyIssues;
